import React, { useEffect, useMemo, useState } from "react";
import * as AppConstants from "AppConstants";
import { checkUserHasBdmRole, getBdmClients } from "app/services/app.state.service";
import { Card, Grid } from "@mui/material";
import { HolidayListView } from "app/components/leave-plans/HolidayListView";
import { ActivityStatsDataGridBase } from "app/components/dashboards/ActivityStatsDataGridBase";
import { loadUnifiedEffortsMonthly } from "app/services/report.service";
import { loadAllManagersFromBackend } from "app/services/manager.service";
import { getDateInDDMMYYYYFormat } from "utils/dateUtil";
import { toClientListOptions } from "utils/clientOptions";
import { resolveOwnerParams } from "utils/effortOwnerParams";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ActivityStatsChartsRow } from "app/components/dashboards/ActivityStatsChartsRow";
import { ConsolidatedReportFilter } from "../reports/ConsolidatedReportFilter";
import { loadMembersForAllClientsFromBackend } from "app/services/member.service";
import { NoDataBanner } from "app/components/dashboards/NoDataBanner";
import "./dashboards.css";

/**
 * A BDM / Client Relationship Manager's dashboard.
 *
 * <p>HRMS-116 point 4: "show them read-only Dashboard except the leave section". So this is the
 * Admin dashboard -- the client-scoped one, since a BDM holds no _manager record to root a query
 * at -- narrowed to their own accounts and with LeavePlanSummaryCard dropped. Holidays stay: a
 * holiday list is not the leave section, and it is read-only for everyone.
 *
 * <p>The first version of this screen composed EffortSummaryView and ExportReportsView alone,
 * which left the efforts-for-period card, the consolidated report and holidays missing, and
 * duplicated Export Reports -- a BDM already reaches that from its own menu entry (routes.js).
 *
 * <p>Read-only by construction rather than by flag: nothing here offers an Add Activity or Add
 * Leave affordance, and ReportDataGridBase drops its whole Actions column for a BDM, so the
 * consolidated report below cannot edit, copy or delete a row either.
 *
 * <p>Only the "Efforts summary for period" card is shown, matching the Manager dashboard. The
 * standalone Effort Summary panel that AdminDashboard also carries is deliberately absent -- it
 * reports the same efforts over the same accounts, so on this screen it was duplication.
 *
 * @since NEXT_VERSION -- HRMS-116
 */
export const BdmDashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);

  const [pendingClientId, setPendingClientId] = useState(AppConstants.ALL_CLIENTS);
  const [pendingPeriod, setPendingPeriod] = useState(defaultPeriod);
  const [pendingUser, setPendingUser] = useState(AppConstants.ALL_USERS);
  const [appliedRange, setAppliedRange] = useState(() => periodRange(defaultPeriod()));
  const [allRows, setAllRows] = useState([]);

  // The accounts naming this user as relationship manager. A BDM has no session client list and no
  // _manager row, so this is the only source of their scope -- reading either of those, as the
  // working-hours screen used to, leaves every dropdown empty and every grid unloaded.
  useEffect(() => {
    let cancelled = false;
    getBdmClients()
      .then((list) => {
        if (!cancelled) setClients(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ownClientIds = useMemo(() => new Set(clients.map((c) => String(c.id))), [clients]);

  // Members and managers come back for every client, so both are narrowed to this BDM's accounts
  // before they reach a dropdown. Scoping in the browser is a display concern only -- the effort
  // query below is scoped server-side by the client ids it sends.
  useEffect(() => {
    if (clients.length === 0) return undefined;
    let cancelled = false;
    loadMembersForAllClientsFromBackend()
      .then((response) => {
        if (cancelled) return;
        const all = Array.isArray(response?.data) ? response.data : [];
        setMembers(all.filter((m) => ownClientIds.has(String(m.client?.id ?? m.clientId))));
      })
      .catch((error) => {
        if (cancelled) return;
        setMembers([]);
        handleAxiosError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [clients.length, ownClientIds]);

  useEffect(() => {
    if (clients.length === 0) return undefined;
    let cancelled = false;
    loadAllManagersFromBackend()
      .then((r) => {
        if (cancelled) return;
        const all = r.status === 204 || !Array.isArray(r.data) ? [] : r.data;
        setManagers(all.filter((m) => ownClientIds.has(String(m.clientId))));
      })
      .catch(() => {
        if (!cancelled) setManagers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clients.length, ownClientIds]);

  const clientOptions = toClientListOptions(clients);

  // Reset the person filter whenever the client changes, so someone from the previous account
  // cannot stay selected against one they do not belong to.
  const onClientChange = (val) => {
    setPendingClientId(val);
    setPendingUser(AppConstants.ALL_USERS);
  };

  const optionMembers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? members
        : members.filter((m) => String(m.client?.id ?? m.clientId) === String(pendingClientId)),
    [members, pendingClientId]
  );
  const optionManagers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? managers
        : managers.filter((m) => String(m.clientId) === String(pendingClientId)),
    [managers, pendingClientId]
  );
  const { memberOptions, managerOptions } = useUserFilterOptions({
    members: optionMembers,
    managers: optionManagers,
  });

  const clientLabelById = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      map[String(c.id)] = c.name ?? c.code ?? String(c.id);
    });
    return map;
  }, [clients]);

  const apply = React.useCallback(
    async (clientId, range, user) => {
      // "All" is this BDM's own accounts, never an absent filter: the effort endpoint reads a
      // missing clientIds as every client in the system.
      const targetClientIds =
        clientId === AppConstants.ALL_CLIENTS ? clients.map((c) => String(c.id)) : [String(clientId)];
      if (targetClientIds.length === 0) {
        setAllRows([]);
        return;
      }
      setIsLoading(true);
      try {
        const response = await loadUnifiedEffortsMonthly({
          fromDate: range.from,
          toDate: range.to,
          clientIds: targetClientIds,
          ...resolveOwnerParams(user, members, [], managers),
        });
        setAllRows(response.status === 204 || !Array.isArray(response.data) ? [] : response.data);
      } catch (error) {
        handleAxiosError(error);
        setAllRows([]);
      } finally {
        setIsLoading(false);
      }
    },
    [clients, members, managers]
  );

  const didInit = React.useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    if (!checkUserHasBdmRole()) return;
    if (clients.length === 0) return;
    didInit.current = true;
    apply(AppConstants.ALL_CLIENTS, appliedRange, AppConstants.ALL_USERS);
  }, [clients, apply, appliedRange]);

  const onApply = () => {
    const range = periodRange(pendingPeriod);
    setAppliedRange(range);
    apply(pendingClientId, range, pendingUser);
  };

  const displayRows = allRows;

  if (!checkUserHasBdmRole()) {
    return <div>You do not have access to this functionality.</div>;
  }

  return (
    <div className="content-container dashboard-container">
      <Grid container rowSpacing={10} columnSpacing={0}>
        <Grid size={12}>
          <Card>
            <MDBox mx={2} mt={-3} py={1} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
              <MDTypography variant="h6" color="white">
                {`Efforts summary for period - ${getDateInDDMMYYYYFormat(
                  appliedRange.from
                )} and ${getDateInDDMMYYYYFormat(appliedRange.to)}`}
              </MDTypography>
            </MDBox>
            <MDBox px={2} pt={3}>
              <EffortFilterBar
                idPrefix="bdm-effort"
                showClient
                clientValue={pendingClientId}
                onClientChange={onClientChange}
                clientOptions={clientOptions}
                showMemberManagerDropdown
                userValue={pendingUser}
                onUserChange={setPendingUser}
                memberOptions={memberOptions}
                managerOptions={managerOptions}
                period={pendingPeriod}
                onPeriodChange={setPendingPeriod}
                onAction={onApply}
              />
            </MDBox>
            {isLoading ? (
              <NoDataBanner message="Loading efforts summary..." />
            ) : clients.length === 0 ? (
              <NoDataBanner message="No accounts are assigned to you yet. Ask an administrator to set you as a client's InOpTra BDM / Client Relationship Manager." />
            ) : displayRows.length === 0 ? (
              <NoDataBanner />
            ) : (
              <>
                <ActivityStatsChartsRow
                  activityStatsData={displayRows}
                  fromDate={appliedRange.from}
                  toDate={appliedRange.to}
                />
                <MDBox mt={2}>
                  <ActivityStatsDataGridBase
                    activityStatsData={displayRows}
                    clientLabelById={clientLabelById}
                  />
                </MDBox>
              </>
            )}
          </Card>
        </Grid>
        <Grid size={12}>
          <ConsolidatedReportFilter role={AppConstants.ROLE_BDM} members={members} />
        </Grid>
        {/* No LeavePlanSummaryCard: HRMS-116 point 4 excludes the leave section. Holidays are not
            part of it -- they are a read-only reference list shown to every other role. */}
        <Grid size={12}>
          <HolidayListView />
        </Grid>
      </Grid>
    </div>
  );
};

export default BdmDashboard;
